import { memo, useEffect, useMemo } from "react";

import { LoadingOverlay } from "../../../../components";
import {
  ReportSummaryHeader,
  ReportSummarySubheader,
  ReportSummaryTopHeader
} from "../../components";

import { useGridConfig } from "../../../search/hooks";
import {
  useGetLayoutDetails,
  useGetLayouts
} from "../../components/layout-editor/hooks";
import { COMPONENTS_MAP } from "../../components/report_summary/consts";
import { useGetReportSummary, useLocalSampleSummaryParams } from "../../hooks";
import { getSelectedLayout } from "../../util";

import { useRlNavContext } from "../../../commons/rl_nav/hooks";
import { ReportSummary } from "../../@types";
import { CanvasRows } from "../../components/layout-editor/@types";
import styles from "./report-summary.module.scss";
import { PDFExport } from "../../components/report_summary/@types/PDFExport";

const getFilteredLayouts = (layout?: CanvasRows, report?: ReportSummary) => {
  if (!layout || !report) return [];
  return layout.filter(
    ({ components }) =>
      components.filter(id => {
        const { Component, filterFn } = COMPONENTS_MAP[id] ?? {};
        if (!Component) return false;
        return !filterFn || filterFn(report);
      }).length
  );
};

const ReportSummaryContainer = ({ isPdfView }: PDFExport) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { setExpanded } = useRlNavContext();

  const { gridConfig } = useGridConfig();
  const { data: layouts } = useGetLayouts();

  const { samples_layout: selected } = gridConfig ?? {};
  // use first rl default as default in case that there is no selected or selected could not be found
  const selectedLayout = getSelectedLayout(selected, layouts);

  const { data: layoutDetails, isPending } = useGetLayoutDetails(
    selectedLayout?.id,
    selectedLayout?.type
  );

  const { data: report, isLoading } = useGetReportSummary(
    hash,
    sourceId,
    relationshipId
  );

  useEffect(() => {
    if (
      selectedLayout?.type === "rl" &&
      selectedLayout?.name === "Tier 1 Analyst"
    ) {
      setExpanded(false);
    } else {
      setExpanded(true);
    }
  }, [selectedLayout?.type, selectedLayout?.name]);

  const filteredLayout = useMemo(
    () => getFilteredLayouts(layoutDetails?.layout, report),
    [layoutDetails?.layout, report]
  );

  if (isLoading || isPending) {
    return (
      <>
        <LoadingOverlay />
        <img
          className="summary-basic-top__placeholder-image"
          src={`${window.tcbase.urls.static}images/report-summary-skeleton.png`}
        />
      </>
    );
  }

  return (
    <div className="flex flex-column flex-gap-4">
      <ReportSummaryTopHeader />
      <ReportSummaryHeader />
      <ReportSummarySubheader />
      <section className="flex flex-column flex-gap-4">
        {filteredLayout?.map((row, index) => (
          <div key={`report-summary-row-${index}`} className={styles.row}>
            {row.components.map(c => {
              const width = row.width / row.components.length;
              const { Component } = COMPONENTS_MAP[c] ?? {};

              return (
                <div key={c} className={styles[`item_${width}`]}>
                  <Component isPdfView={isPdfView} />
                </div>
              );
            })}
          </div>
        ))}
      </section>
    </div>
  );
};

export default memo(ReportSummaryContainer);
